import {
    Bell,
    Gamepad2,
    Gift,
    Package,
    PiggyBank,
    Plus,
    Shield,
    Star,
    Swords,
    User,
    Users,
    Wallet,
    Zap,
} from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';

/**
 * Single source of truth for navigation / UI iconography. Components in the
 * app import from here (`<Icon name="games" />`) instead of pulling
 * lucide-react directly, so we keep stroke width / sizing consistent and
 * can swap the icon pack later without touching every screen.
 *
 * Emoji that are *content* (game elements, anonymous avatars, brand glyphs
 * like the TON diamond) are deliberately left alone — those convey meaning
 * a flat outline can't carry.
 */
type IconName =
    | 'bell'
    | 'games'
    | 'gift'
    | 'box'
    | 'piggy'
    | 'plus'
    | 'shield'
    | 'star'
    | 'sword'
    | 'profile'
    | 'people'
    | 'wallet'
    | 'lightning';

const MAP: Record<IconName, ComponentType<SVGProps<SVGSVGElement>>> = {
    bell:      Bell,
    games:     Gamepad2,
    gift:      Gift,
    box:       Package,
    piggy:     PiggyBank,
    plus:      Plus,
    shield:    Shield,
    star:      Star,
    sword:     Swords,
    profile:   User,
    people:    Users,
    wallet:    Wallet,
    lightning: Zap,
};

type Props = {
    name: IconName;
    size?: number;
    /** Stroke width — lucide default is 2; 1.75 looks slightly lighter and
     *  pairs better with our 900-weight type. */
    strokeWidth?: number;
    className?: string;
    color?: string;
    /** When true, fills the icon with `currentColor` — used for the balance
     *  star pill where the outline alone is too quiet. */
    filled?: boolean;
    style?: React.CSSProperties;
    'aria-hidden'?: boolean;
};

export function Icon({
    name,
    size = 18,
    strokeWidth = 1.85,
    className,
    color,
    filled,
    style,
    'aria-hidden': ariaHidden = true,
}: Props) {
    const Cmp = MAP[name];
    return (
        <Cmp
            width={size}
            height={size}
            strokeWidth={strokeWidth}
            className={className}
            color={color}
            fill={filled ? 'currentColor' : 'none'}
            style={style}
            aria-hidden={ariaHidden}
        />
    );
}
